import VerificationRule from './VerificationRule';
import {
  CONFIDENCE_RAMP,
  MEASURED_ESTIMATED_BOUNDARY,
  VERIFICATION_LABELS,
  type LegendaLang,
  type Verification,
} from './vocabulary';

const CAPTION: Record<LegendaLang, { measured: string; estimated: string }> = {
  ru: { measured: '← ИЗМЕРЕНО И ПРОВЕРЕНО', estimated: 'ОЦЕНЕНО МАШИНОЙ →' },
  hy: { measured: '← ՉԱՓՎԱԾ ԵՎ ՀԱՍՏԱՏՎԱԾ', estimated: 'ԳՆԱՀԱՏՎԱԾ ՄԵՔԵՆԱՅԻ ԿՈՂՄԻՑ →' },
  en: { measured: '← MEASURED AND REVIEWED', estimated: 'MACHINE-ESTIMATED →' },
};

export interface ConfidenceMeterProps {
  /**
   * Current position. Values off the ramp (`conflict`, `unknown`, `rejected`)
   * render no marker — they are categorical and have no place on a scale.
   */
  status: Verification;
  lang: LegendaLang;
}

/**
 * Five discrete notches with a hard divider, never a continuous bar.
 *
 * The brief asks that the distance between *measured* and *guessed* feel
 * categorical rather than gradual. A progress-style bar would say the opposite:
 * that a low-confidence candidate is a slightly shorter version of a reviewed
 * fact. So the scale is stepped, and the boundary between reviewed and
 * machine-estimated is drawn as a rule rather than a gradient.
 */
export default function ConfidenceMeter({ status, lang }: ConfidenceMeterProps) {
  const caption = CAPTION[lang];

  return (
    <div>
      <div className="lg-meter" role="img" aria-label={VERIFICATION_LABELS[status][lang]}>
        {CONFIDENCE_RAMP.map((step, index) => {
          const classes = ['lg-meter__step'];
          if (index === MEASURED_ESTIMATED_BOUNDARY - 1) classes.push('lg-meter__step--boundary');
          if (index >= MEASURED_ESTIMATED_BOUNDARY) classes.push('lg-meter__step--estimated');

          return (
            <div
              key={step}
              className={classes.join(' ')}
              aria-current={step === status ? 'true' : undefined}
            >
              <VerificationRule status={step} lang={lang} labelInLegend>
                <span style={{ display: 'block', height: 16 }} />
              </VerificationRule>
              <div className="lg-meter__label">{VERIFICATION_LABELS[step][lang]}</div>
            </div>
          );
        })}
      </div>
      <div className="lg-meter__caption">
        <span>{caption.measured}</span>
        <span>{caption.estimated}</span>
      </div>
    </div>
  );
}
